import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';

import { RecurringSubscriptionStatus, SubscriptionOrderStatus } from 'chaika-enums';

import { useSubscriptions } from '@common/Chat/hooks/useSubscriptions';
import { isActivatedSubscriptionOrder } from '@patient/utils/isActivatedSubscriptionOrder';
import { useUsersMap } from '@user/hooks/useUsersMap';
import { getInitials } from '@user/utils/getInitials';
import { isDefined } from '@utils/isDefined';

interface Params {
    patientId: string;
}

interface Data {
    isLoaded: boolean;
    hasActiveSubscription: boolean;
    hasTrialSubscription: boolean;
    subscriptionDoctorNames: string[];
    reload(): Promise<void>;
}

export function usePatientSubscriptionStatus({ patientId }: Params): Data {
    const { subscriptionOrders, recurringSubscriptions, isLoaded, reload } = useSubscriptions({ patientId });

    const activeSubscriptionOrders = subscriptionOrders.filter(
        order => order.status === SubscriptionOrderStatus.Active,
    );
    const activeRecurringSubscriptions = recurringSubscriptions.filter(
        subscription => subscription.status === RecurringSubscriptionStatus.Active,
    );

    const hasActiveSubscription =
        subscriptionOrders.some(isActivatedSubscriptionOrder) || activeRecurringSubscriptions.length > 0;

    const hasTrialSubscription =
        activeSubscriptionOrders.some(order => isNil(order.paymentId)) ||
        activeRecurringSubscriptions.some(subscription => subscription.isTrial);

    const subscriptionDoctorIds = uniq(
        [
            ...activeSubscriptionOrders.map(order => order.doctorId),
            ...activeRecurringSubscriptions.map(subscription => subscription.doctorId),
        ].filter(isDefined),
    );
    const subscriptionDoctorsById = useUsersMap(subscriptionDoctorIds);
    const subscriptionDoctorNames = subscriptionDoctorIds
        .map(id => {
            const doctor = subscriptionDoctorsById.get(id);

            return doctor && getInitials(doctor);
        })
        .filter(isDefined);

    return { isLoaded, hasActiveSubscription, hasTrialSubscription, subscriptionDoctorNames, reload };
}
